import React from 'react';
import {
  Check,
  ChevronDown,
  ClipboardList,
  Euro,
  PartyPopper,
  Plus,
  QrCode,
  StickyNote,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import i18n from '@/utils/i18n';
import { DEFAULT_CURRENCY } from '@/utils/price';

import type { ContainerType } from '../../ContainerComponents/ContainerPage';
import ContainerTypeEditForm from './ContainerTypeEditForm';
import ContainerPriceEditor, {
  type PriceRawAccessors,
} from './ContainerPriceEditor';
import SegmentedControl from './SegmentedControl';
import type { SystemType } from './constants';
import type { EquivalenceOption } from './useContainerTypesAdmin';

export type EditorTab = 'info' | 'open' | 'event';

export const SYSTEM_TYPE_BY_TAB: Record<
  Exclude<EditorTab, 'info'>,
  SystemType
> = {
  open: 'OpenSystem',
  event: 'Event',
};

type FacetOption = { value: string; label: string };

export type ContainerTypeEditorPanelProps = {
  /** The open container type, or the draft in create mode. */
  item: ContainerType | null;
  isDraft: boolean;
  variant: 'desktop' | 'mobile';
  tab: EditorTab;
  onTabChange: (tab: EditorTab) => void;
  onClose: () => void;

  // form context
  language: string;
  disabled: boolean;
  containerClasses: FacetOption[];
  containerTypeGroups: FacetOption[];
  countries: FacetOption[];
  brandings: string[];
  legalOwners: string[];
  equivalenceOptions: EquivalenceOption[];
  duplicateSkus: string[];
  displayName: (ct: ContainerType) => string;

  // form callbacks (already draft-aware)
  onField: (attr: string, value: unknown) => void;
  onCountryToggle: (code: string) => void;
  onEquivalentToggle: (typeId: number) => void;
  onAddBranding: (value: string) => void;
  onAddLegalOwner: (value: string) => void;
  onReorder: (direction: 1 | -1) => void;
  onUploadImage: () => void;

  // pricing (already draft-aware)
  priceAccessors: PriceRawAccessors;
  /** Drafts only carry open-system prices; the event tab shows a note. */
  draftEventPricingNote?: boolean;

  // header actions (hidden for drafts)
  onDownloadQr: () => void;
  onPrintLabel: () => void;

  // footer
  saveHint: string;
  saveDisabled: boolean;
  onSave: () => void;
  createDisabled: boolean;
  creating: boolean;
  onCreate: () => void;
};

/**
 * The edit sheet's content — shared by the desktop right-hand sheet and the
 * mobile full-screen editor. Three peer tabs: Information · Open-system
 * pricing · Event pricing; drafts swap the footer to Cancel / Create.
 */
export default function ContainerTypeEditorPanel(
  props: ContainerTypeEditorPanelProps,
) {
  const { item, isDraft, variant, tab, onTabChange, onClose } = props;
  const [selectedCurrency, setSelectedCurrency] =
    React.useState<string>(DEFAULT_CURRENCY);

  // A new editor session (other container / fresh draft) resets to EUR.
  const itemKey = item == null ? null : `${isDraft ? 'draft' : item.id}`;
  React.useEffect(() => {
    setSelectedCurrency(DEFAULT_CURRENCY);
  }, [itemKey]);

  const open = item != null;
  const isMobile = variant === 'mobile';

  const tabOptions = [
    {
      value: 'info' as const,
      label: i18n.t(
        isMobile
          ? 'EditContainerInfo:tabInfoShort'
          : 'EditContainerInfo:tabInfo',
      ),
      icon: <ClipboardList size={isMobile ? 14 : 16} />,
    },
    {
      value: 'open' as const,
      label: i18n.t(
        isMobile
          ? 'EditContainerInfo:tabOpenShort'
          : 'EditContainerInfo:tabOpen',
      ),
      icon: <Euro size={isMobile ? 14 : 16} />,
    },
    {
      value: 'event' as const,
      label: i18n.t(
        isMobile
          ? 'EditContainerInfo:tabEventShort'
          : 'EditContainerInfo:tabEvent',
      ),
      icon: <PartyPopper size={isMobile ? 14 : 16} />,
    },
  ];

  const body = item && (
    <>
      {/* Tab row */}
      <div className="tw:flex-none tw:overflow-x-auto tw:border-b tw:border-border tw:px-5 tw:py-3">
        <SegmentedControl
          options={tabOptions}
          value={tab}
          onChange={onTabChange}
          grow={isMobile}
          size={isMobile ? 'sm' : 'md'}
        />
      </div>

      {/* Body */}
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
        {tab === 'info' && (
          <ContainerTypeEditForm
            item={item}
            isDraft={isDraft}
            language={props.language}
            disabled={props.disabled}
            containerClasses={props.containerClasses}
            containerTypeGroups={props.containerTypeGroups}
            countries={props.countries}
            brandings={props.brandings}
            legalOwners={props.legalOwners}
            equivalenceOptions={props.equivalenceOptions}
            duplicateSkus={props.duplicateSkus}
            onField={props.onField}
            onCountryToggle={props.onCountryToggle}
            onEquivalentToggle={props.onEquivalentToggle}
            onAddBranding={props.onAddBranding}
            onAddLegalOwner={props.onAddLegalOwner}
            onReorder={props.onReorder}
            onUploadImage={props.onUploadImage}
          />
        )}
        {tab !== 'info' &&
          (isDraft && tab === 'event' && props.draftEventPricingNote ? (
            <div className="tw:px-5 tw:py-5 tw:text-sm tw:text-muted-foreground">
              {i18n.t('EditContainerInfo:draftEventPricesNote')}
            </div>
          ) : (
            <ContainerPriceEditor
              systemType={SYSTEM_TYPE_BY_TAB[tab]}
              disabled={props.disabled}
              selectedCurrency={selectedCurrency}
              onSelectCurrency={setSelectedCurrency}
              getRaw={props.priceAccessors.getRaw}
              setRaw={props.priceAccessors.setRaw}
            />
          ))}
      </div>

      {/* Footer */}
      <div
        className={
          'tw:flex tw:flex-none tw:items-center tw:justify-between tw:gap-2.5 tw:border-t tw:border-border tw:bg-background tw:px-5 tw:py-3.5' +
          (isMobile ? ' tw:pb-[max(16px,env(safe-area-inset-bottom))]' : '')
        }
      >
        {isDraft ? (
          <>
            <span className="tw:min-w-0 tw:text-xs tw:text-muted-foreground">
              {i18n.t('EditContainerInfo:draftPricesHint')}
            </span>
            <div className="tw:flex tw:flex-none tw:gap-2">
              <Button variant="outline" onClick={onClose}>
                {i18n.t('Common:cancel')}
              </Button>
              <Button
                onClick={props.onCreate}
                disabled={props.createDisabled || props.creating}
              >
                <Plus size={15} />
                {props.creating
                  ? i18n.t('Common:saving')
                  : i18n.t('EditContainerInfo:createContainerType')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className="tw:min-w-0 tw:text-xs tw:text-muted-foreground">
              {props.saveHint}
            </span>
            <div className="tw:flex tw:flex-none tw:gap-2">
              {!isMobile && (
                <Button variant="outline" onClick={onClose}>
                  {i18n.t('Common:close')}
                </Button>
              )}
              <Button onClick={props.onSave} disabled={props.saveDisabled}>
                <Check size={15} />
                {i18n.t('Common:saveChanges')}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );

  const header = item && (
    <div
      className={
        'tw:flex tw:flex-none tw:items-start tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:px-5 tw:pb-3.5 tw:pt-4.5'
      }
    >
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-1">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="tw:-ml-2 tw:mt-0.5"
            aria-label={i18n.t('Common:close')}
            onClick={onClose}
          >
            <ChevronDown size={20} />
          </Button>
        )}
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
          <span className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            {i18n.t(
              isDraft
                ? 'EditContainerInfo:newEyebrow'
                : 'EditContainerInfo:editEyebrow',
            )}
          </span>
          <SheetTitle className="tw:truncate tw:text-lg tw:font-semibold">
            {props.displayName(item) ||
              (isDraft ? i18n.t('EditContainerInfo:untitledContainer') : '')}
          </SheetTitle>
          <SheetDescription className="tw:sr-only">
            {i18n.t(
              isDraft
                ? 'EditContainerInfo:newEyebrow'
                : 'EditContainerInfo:editEyebrow',
            )}
          </SheetDescription>
        </div>
      </div>
      <div className="tw:flex tw:flex-none tw:items-center tw:gap-1">
        {!isDraft && (
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={i18n.t('EditContainerInfo:downloadQr')}
              onClick={props.onDownloadQr}
            >
              <QrCode size={16} />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={i18n.t('EditContainerInfo:printLabel')}
              onClick={props.onPrintLabel}
            >
              <StickyNote size={16} />
            </Button>
          </>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={i18n.t('Common:close')}
            onClick={onClose}
          >
            <X size={18} />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        showCloseButton={false}
        className={
          isMobile
            ? 'tw:gap-0 tw:rounded-none tw:border-t-0 tw:p-0 tw:data-[side=bottom]:h-dvh tw:data-[side=bottom]:max-h-dvh'
            : // The variant stack must match the Sheet's own
              // data-[side=right]:sm:max-w-sm class, otherwise that 384px cap
              // wins on specificity and the sheet renders far too narrow.
              'tw:w-[560px] tw:max-w-[94%] tw:gap-0 tw:p-0 tw:data-[side=right]:sm:max-w-[560px]'
        }
      >
        {item && (
          <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-col">
            {header}
            {body}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
